import { Radio, QrCode, MonitorSmartphone, Radar, ShieldCheck, LayoutDashboard } from "lucide-react";
import { FadeUp, ChapterHeading } from "./Shared";

const tiles = [
  {
    icon: Radio,
    title: "Realtime sentiment stream",
    body: "Live mood signals from verified, physically-present visitors. Seconds from scan to dashboard.",
    span: "lg:col-span-3 lg:row-span-2",
    accent: true,
  },
  {
    icon: QrCode,
    title: "PulsePoints — print",
    body: "Elegant print touchpoints bound to micro-locations. Know exactly where mood shifts.",
    span: "lg:col-span-2",
  },
  {
    icon: MonitorSmartphone,
    title: "PulseScreens — kiosk",
    body: "One-tap sentiment touchpoints for concourses, lobbies, queues and platforms.",
    span: "lg:col-span-2",
  },
  {
    icon: Radar,
    title: "Geofenced insights",
    body: "Every data point tied to your exact venue boundary. No off-site noise.",
    span: "lg:col-span-2",
  },
  {
    icon: ShieldCheck,
    title: "Credible by design",
    body: "One person, one place, one signal. No bots, no brigading, no duplicates.",
    span: "lg:col-span-2",
  },
  {
    icon: LayoutDashboard,
    title: "The Command Centre",
    body: "One view to understand your audience sentiment on the ground: live mood index by zone and hour, urgency flags surfaced first, export-ready data points for stakeholders and tenants.",
    span: "lg:col-span-4",
  },
];

const metrics = [
  { value: "<5s", label: "SCAN-TO-SIGNAL LATENCY" },
  { value: "100%", label: "FIRST-PARTY DATA" },
  { value: "24/7", label: "ALWAYS-ON CAPTURE" },
  { value: "6", label: "SIGNAL FLAVORS" },
  { value: "0", label: "GUESSWORK", volt: true },
];

export function Offering() {
  return (
    <section id="offering" className="py-28 lg:py-36" data-testid="section-offering">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <ChapterHeading number="03" title="THE OFFERING" />
        <div className="grid lg:grid-cols-6 gap-px bg-white/10 border border-white/10">
          {tiles.map((t, i) => (
            <FadeUp key={t.title} delay={i * 0.08} className={`${t.span} bg-[#0a0a0a]`}>
              <div className={`h-full p-8 lg:p-10 group transition-colors duration-300 ${t.accent ? "hover:bg-[#141400]" : "hover:bg-[#101010]"}`}>
                <t.icon size={26} strokeWidth={1.5} className={`${t.accent ? "text-[#E5FF00]" : "text-zinc-400 group-hover:text-[#E5FF00]"} transition-colors mb-8`} />
                <h3 className="font-display font-bold text-xl lg:text-2xl mb-3">{t.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed max-w-sm">{t.body}</p>
                {t.accent && (
                  <p className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] text-[#E5FF00]">CORE SIGNAL · LIVE</p>
                )}
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-32" data-testid="section-metrics">
          <ChapterHeading number="04" title="PURE DATA POINTS" />
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {metrics.map((m, i) => (
              <FadeUp key={m.label} delay={i * 0.08} className="bg-[#0a0a0a]">
                <div className="p-8 lg:p-10 text-left" data-testid={`metric-${m.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <p className={`font-mono-tech font-bold text-4xl lg:text-5xl ${m.volt ? "text-[#E5FF00]" : "text-white"}`}>{m.value}</p>
                  <p className="mt-4 font-mono-tech text-[10px] tracking-[0.25em] text-zinc-500">{m.label}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
